import { LinearGradient } from 'expo-linear-gradient';
import React, { useEffect, useRef, useState } from 'react';
import {
  Image,
  Platform,
  ScrollView,
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

import colors from 'constants/colors';
import { COMBINI_FEE } from 'shared/constants/fees';
import IMAGES from 'shared/constants/images';
import Button from 'shared/modules/common/components/atoms/Button';
import Icon from 'shared/modules/common/components/atoms/Icon';
import generateID from 'shared/modules/common/utils/generate-id';

type Props = {
  style?: StyleProp<ViewStyle>;
  activeScreenIndex?: number;
  onItemPress?: (item: string) => void;
  isShoppingEnabled?: boolean;
};

const PAGES = [
  (props: Props) => (
    <>
      <View style={style.headerImageWrapper}>
        <Image
          source={{ uri: IMAGES.helpHeader1 }}
          style={style.headerImage}
          resizeMode="cover"
        />
      </View>
      <Text style={style.textTitle}>翌月あと払いとは</Text>
      <Text style={style.text}>
        メールアドレスと携帯番号だけで、お買い物を楽しめます。お支払いはまとめて翌月でOK。
      </Text>
      <Text style={[style.textTitle, style.textSubTitle]}>
        翌月あと払いの使い方
      </Text>
      <View style={style.listItem}>
        <NumberPoint number={1} />
        <Text style={style.text}>
          ペイディが使えるお店で、欲しい商品を選ぶ。
        </Text>
      </View>
      <View style={style.listItem}>
        <NumberPoint number={2} />
        <Text style={style.text}>
          お支払い方法で「あと払い（ペイディ）」を選択。
        </Text>
      </View>
      <View style={style.listItem}>
        <NumberPoint number={3} />
        <Text style={style.text}>メールアドレスと携帯番号を入力。</Text>
      </View>
      <View style={style.listItem}>
        <NumberPoint number={4} />
        <Text style={style.text}>
          SMSで届く4桁の認証コードを入力して、お買い物完了です。
        </Text>
      </View>
      <LearnMore {...props} />
    </>
  ),
  (props: Props) => (
    <>
      <View style={style.headerImageWrapper}>
        <Image
          source={{ uri: IMAGES.helpHeader2 }}
          style={style.headerImage}
          resizeMode="cover"
        />
      </View>
      <Text style={style.textTitle}>お支払い方法</Text>
      <Text style={style.text}>
        翌月1日から3日までの間に、メールとSMSでご請求金額をお知らせします。下記３つのお支払い方法のうち、ひとつを選んでお支払いください。
      </Text>
      <View style={style.listItem}>
        <BulletPoint />
        <View>
          <Text style={[style.text, style.textLinkItemTitle]}>
            コンビニ払い
          </Text>
          <Text
            style={style.text}
          >{`最大税込${COMBINI_FEE}円の手数料がかかります。`}</Text>
        </View>
      </View>
      <View style={style.listItem}>
        <BulletPoint />
        <View>
          <Text style={[style.text, style.textLinkItemTitle]}>銀行振込</Text>
          <Text style={style.text}>手数料は銀行によって異なります。</Text>
        </View>
      </View>
      <View style={style.listItem}>
        <BulletPoint />
        <View style={{ flexShrink: 1 }}>
          <Text style={[style.text, style.textLinkItemTitle]}>口座振替</Text>
          <Text style={style.text}>
            手数料は無料です。初めて使う場合には事前登録が必要です。毎月自動的に引き落としされるので支払いの手間が省けます。
            {'\n'}
            月末までに登録完了した場合、（みずほ銀行の場合は20日までに登録完了した場合）原則として翌月請求分以降が口座振替の対象です。
          </Text>
        </View>
      </View>
      <Button
        title="銀行口座を登録する"
        onPress={() => props.onItemPress?.('bank-transfer')}
        style={style.button}
        id="register-bank-account-button"
      />
      <LearnMore {...props} />
    </>
  ),
  (props: Props) => (
    <>
      <View style={style.headerImageWrapper}>
        <Image
          source={{ uri: IMAGES.helpHeader3 }}
          style={style.headerImage}
          resizeMode="cover"
        />
      </View>
      <Text style={style.textTitle}>{`３・6・12回あと払い`}</Text>
      <View style={style.listItem}>
        <Text style={style.text}>
          分割手数料無料*で、お支払いを3・6・12回に分けることができます。お支払い総額はそのままで月々のお支払いを調整できるので、よりかしこく計画的にお買い物を楽しめます。
        </Text>
      </View>
      <TouchableOpacity onPress={() => props.onItemPress?.('npay')}>
        <Text style={[style.textLink, style.textSecondary, style.text]}>
          {`6・12回あと払いが使えるお店を見る`}
        </Text>
      </TouchableOpacity>
      <Text style={[style.textTitle, style.textSubTitle]}>
        {`3・6・12回あと払いの使い方`}
      </Text>
      <View style={style.listItem}>
        <NumberPoint number={1} />
        <Text style={style.text}>ペイディアプリから本人確認する**。</Text>
      </View>
      <View style={style.listItem}>
        <NumberPoint number={2} />
        <Text style={style.text}>
          ネットショップで購入時に支払い方法でご希望の回数を選択するか、購入後にアプリからご希望の回数に変更して完了。
        </Text>
      </View>
      <Text style={style.textSecondary}>
        {`*銀行振込・口座振替のみ手数料無料\n** 別途審査があります\n\n`}
        {`※３回あと払いは1回のお買い物につき、Amazonは3円以上、その他のショップでは3,000円以上のお支払いで利用可能です。6回あと払いは1回のお買い物につき、Amazonは6円以上、その他のショップでは6,000円以上のお支払いで利用可能です。12回あと払いは1回のお買い物につき、12,000円以上でご利用いただけます。一部のショップでは3・6・12回あと払いはご利用いただけません。\n\n※ペイディカードで３回あと払いを利用する場合は、店舗での購入時に分割払いを選択せず、一括払いで購入後、ペイディアプリから３回あと払いに変更してください。\n\n\n`}
      </Text>
      <LearnMore {...props} />
    </>
  ),
  (props: Props) => {
    const onPress = () => props.onItemPress?.('shopping');

    const content = props.isShoppingEnabled ? (
      <>
        <Text style={style.textTitle}>お買い物</Text>
        <View style={style.listItem}>
          <BulletPoint />
          <Text style={style.text}>
            ペイディで購入できるアイテムを簡単に見つけることが可能で、アプリ内でスムーズに購入できます。
          </Text>
        </View>
        <View style={style.listItem}>
          <BulletPoint />
          <Text style={style.text}>
            好きなお店やアイテムを見つけて「お気に入り」に追加したり、お得なキャンペーン情報をチェックしたりできます。
          </Text>
        </View>
        <View style={style.listItem}>
          <BulletPoint />
          <Text style={style.text}>
            本人確認をしてペイディカードを発行すると、Visaマークのあるネットショップでもペイディが利用可能になります。
          </Text>
        </View>
        <Button
          title="お買い物を見る"
          onPress={onPress}
          style={style.button}
          id="view-shopping-button"
        />
      </>
    ) : (
      <>
        <Text style={style.textTitle}>使えるお店</Text>
        <Text style={style.text}>
          ファッション、コスメ、家電、デジタルコンテンツなど、70万店舗でご利用いただけます。
        </Text>
        <Button
          title="お買い物を見る"
          onPress={onPress}
          style={style.button}
          id="view-shopping-button"
        />
        <Text style={style.text}>
          さらに、本人確認をして「どこでもペイディ」に登録すると、Yahoo!、メルカリ、PayPalなど、2,900万店舗でご利用いただけるようになります。
        </Text>
      </>
    );

    return (
      <>
        <View style={style.headerImageWrapper}>
          <Image
            source={{ uri: IMAGES.helpHeader4 }}
            style={style.headerImage}
            resizeMode="cover"
          />
        </View>
        {content}
        <LearnMore {...props} />
      </>
    );
  },
];

const Help = (props: Props) => {
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const scrollViewRef = useRef<ScrollView | null>(null);

  const scrollToTop = () => scrollViewRef.current?.scrollTo({ x: 0, y: 0 });

  useEffect(scrollToTop, [currentPageIndex]);

  useEffect(() => {
    setTimeout(scrollToTop);
    const index = props.activeScreenIndex ? props.activeScreenIndex : 0;

    setCurrentPageIndex(index > -1 ? index : 0);
  }, [props.activeScreenIndex]);

  return (
    <View style={[style.root, props.style]}>
      {currentPageIndex > 0 && (
        <TouchableOpacity
          style={style.paginationArrow}
          onPress={() => setCurrentPageIndex(currentPageIndex - 1)}
          /** Generate HTML and Test IDs  */
          {...generateID(`help-previous`)}
          /** Platform-specific props */
          {...Platform.select({
            web: {
              accessibilityRole: 'button',
            },
          })}
        >
          <Icon name="back" size="44" />
        </TouchableOpacity>
      )}
      {currentPageIndex < PAGES.length - 1 && (
        <TouchableOpacity
          style={[style.paginationArrow, style.paginationArrowRight]}
          onPress={() => setCurrentPageIndex(currentPageIndex + 1)}
          /** Generate HTML and Test IDs  */
          {...generateID(`help-next`)}
          /** Platform-specific props */
          {...Platform.select({
            web: {
              accessibilityRole: 'button',
            },
          })}
        >
          <Icon name="back" size="44" />
        </TouchableOpacity>
      )}
      <ScrollView
        contentContainerStyle={style.scrollViewContainer}
        ref={(ref) => (scrollViewRef.current = ref)}
      >
        {PAGES[currentPageIndex](props)}
      </ScrollView>
      <View style={style.pagination}>
        {PAGES.map((_, index) => (
          <LinearGradient
            key={index}
            colors={
              index === currentPageIndex
                ? ['#E5258C', '#823CD7']
                : ['#979797', '#979797']
            }
            /** will do an approximately 135-degree gradient */
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 6 }}
            style={{
              width: 16,
              height: 16,
              borderRadius: 16,
              marginHorizontal: 16,
            }}
          />
        ))}
      </View>
    </View>
  );
};

const BulletPoint = () => (
  <Icon name="bullet" size="12" style={style.iconBullet} />
);

const NumberPoint = ({ number }: { number: number }) => (
  <View style={style.numberPoint}>
    <Text style={style.numberPointText}>{number}</Text>
  </View>
);

const LearnMore = (props: Props) => (
  <Text style={[style.text, style.learnMore]}>
    詳しくは「
    {
      <TouchableOpacity onPress={() => props.onItemPress?.('faq')}>
        <Text style={style.textLink}>よくある質問</Text>
      </TouchableOpacity>
    }
    」{'\n'}
    ページをご覧ください。
  </Text>
);

const style = StyleSheet.create({
  root: {
    overflow: 'hidden',
    flex: 1,
    position: 'relative',
  },
  scrollViewContainer: {
    padding: 16,
    backgroundColor: colors.white,
    minHeight: '100%',
  },
  headerImageWrapper: {
    marginHorizontal: -16,
    marginBottom: 32,
  },
  headerImage: {
    width: '100%',
    height: '100%',
    minHeight: 352,
  },
  text: {
    marginBottom: 24,
  },
  textTitle: {
    fontSize: 28,
    fontWeight: '600',
    marginBottom: 32,
    lineHeight: 32,
  },
  textSubTitle: {
    fontSize: 20,
  },
  textSecondary: {
    fontSize: 13,
  },
  textLink: {
    color: '#e5258c',
    fontWeight: 'bold',
  },
  textLinkItemTitle: {
    fontWeight: 'bold',
    marginBottom: 8,
  },
  listItem: {
    flexDirection: 'row',
  },
  iconBullet: {
    margin: 6,
  },
  numberPoint: {
    width: 28,
    height: 28,
    borderColor: '#101825',
    borderWidth: 1,
    borderRadius: 14,
    marginRight: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  numberPointText: {
    fontSize: 12,
  },
  learnMore: {
    textAlign: 'center',
    fontWeight: 'bold',
  },
  pagination: {
    height: 88,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#F8F8F8',
  },
  paginationArrow: {
    position: 'absolute',
    top: 168,
    left: 0,
    zIndex: 1,
  },
  paginationArrowRight: {
    right: 0,
    left: 'initial',
    transform: [
      {
        rotate: '180deg',
      },
    ],
  },
  button: {
    marginBottom: 32,
  },
});

export default Help;
